import "./styles.scss";

import moment from "moment";
import { useState } from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useApp } from "context/app";
import { useNotifications } from "context/notifications";

import Button from "components/Button";
import Dropdown from "components/Dropdown";
import IconButton from "components/IconButton";
import CheckButton from "components/CheckButton";
import DropdownCard from "components/DropdownCard";
import DropdownButton from "components/DropdownButton";

import notification_render, {
  getNotificationTitle,
  isNewTaskNotification,
} from "libs/notification_render";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as NotificationIcon } from "assets/icons/menu/notification.svg";

const mainClass = "notification-dropdown";

const Notifications = ({ setWebsiteInfoId }) => {
  const { user } = useApp();
  const { t } = useTranslation();

  const { data, readHandler } = useNotifications();
  const [dropdown_key, setDropdownKey] = useState(0);

  const unreaded_count = data?.reduce((a, b) => a + (!!!b?.status ? 1 : 0), 0);
  const unreaded_notifications = data
    ?.filter((item) => item?.status === 0)
    ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    ?.slice(0, 4);

  const closeHandler = () => {
    setDropdownKey((prev) => prev + 1);
  };

  return (
    <Dropdown
      custom={true}
      Button={({ active }) => (
        <DropdownButton
          active={active}
          variant="wide"
          component={
            <IconButton
              icon={<NotificationIcon />}
              badgeContent={unreaded_count}
              variant={!!!unreaded_count ? "gray" : null}
            />
          }
        />
      )}
      onclose={dropdown_key}
    >
      <DropdownCard>
        <div
          className={classnames(mainClass, {
            [`${mainClass}--empty`]: unreaded_notifications?.length === 0,
          })}
        >
          <div className={`${mainClass}__header`}>
            <h2>{t("Notifications")}</h2>
            {unreaded_notifications?.length > 0 && (
              <button onClick={() => readHandler("all")}>
                {t("Mark all as read")}
              </button>
            )}
          </div>
          <div className={`${mainClass}__content`}>
            {unreaded_notifications?.length > 0 ? (
              unreaded_notifications?.map(
                ({
                  _id,
                  template,
                  href,
                  created_at,
                  status,
                  custom_text,
                  context,
                  website,
                  task,
                }) => {
                  const notification = notification_render(
                    template,
                    context,
                    t,
                  );

                  const newWebsiteComment =
                    template === null || template === 24;
                  const hasLink =
                    !!href &&
                    href.indexOf("undefined") === -1 &&
                    (newWebsiteComment
                      ? user?.type === "client"
                        ? website?._id
                        : false
                      : true);
                  const hasAction =
                    newWebsiteComment &&
                    user?.type !== "client" &&
                    !!website?._id;
                  const Tag = hasAction ? "div" : hasLink ? Link : "div";

                  return (
                    <Tag
                      key={_id}
                      to={
                        hasAction
                          ? undefined
                          : hasLink
                            ? newWebsiteComment
                              ? `/websites/${website?._id}`
                              : href
                            : undefined
                      }
                      onClick={() => {
                        if (hasAction) return;

                        closeHandler();
                        readHandler(_id, 1);
                      }}
                      className={classnames(`${mainClass}__content__item`, {
                        [`${mainClass}__content__item--disabled`]: !!status,
                      })}
                    >
                      <div className={`${mainClass}__content__item__wrapper`}>
                        <div className={`${mainClass}__content__item__status`}>
                          <span
                            className={
                              template === 17 ||
                              template === 18 ||
                              template === 19 ||
                              template === 20
                                ? "yellow"
                                : template === 28
                                  ? "blue"
                                  : "green"
                            }
                          />
                        </div>
                        <div className={`${mainClass}__content__item__info`}>
                          {isNewTaskNotification(template) ? (
                            <span>
                              {getNotificationTitle(
                                { template, context, task },
                                t,
                              )}
                            </span>
                          ) : (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: !!template
                                  ? notification?.text
                                  : custom_text,
                              }}
                            />
                          )}
                          <small>{`${moment(created_at)?.format("LL")}`}</small>
                        </div>
                        <CheckButton
                          checked={status === 1}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            readHandler(_id, 1);
                          }}
                        />
                        {(hasAction || hasLink) && (
                          <div className={`${mainClass}__content__item__arrow`}>
                            <ArrowIcon />
                          </div>
                        )}
                      </div>
                      {hasAction && (
                        <button
                          className={`${mainClass}__content__item__action`}
                          onClick={() => {
                            setWebsiteInfoId(website._id);
                            closeHandler();
                            readHandler(_id, 1);
                          }}
                        />
                      )}
                    </Tag>
                  );
                },
              )
            ) : (
              <div key="empty" className={`${mainClass}__content__placeholder`}>
                <h3>{t("You haven't got any notifications")}</h3>
                <span>{t("You are up to date.")}</span>
              </div>
            )}
          </div>
          {!!unreaded_notifications?.length > 0 && (
            <div className={`${mainClass}__bottom`}>
              <Button fullWidth href="/notifications" onClick={closeHandler}>
                {t("Show all")}
              </Button>
            </div>
          )}
        </div>
      </DropdownCard>
    </Dropdown>
  );
};

export default Notifications;
